import API from "serviceshift-ui/api-client";

// Shapes from the Webhooks hub API contract (Admin::V4 webhooks, ENG-887 and
// ENG-886). Every resource is addressed by its public id (whep_…), and the
// full endpoint URL only comes back from show, create and update: it is a
// credential, so nothing here keeps it in the store.
//
// Every request opts out of the global error flash (ignoreError): the hub
// shows its own plain errors, inline where a form field is at fault.

export type EndpointStatus = "active" | "paused" | "disabled";
export type EndpointHealth =
  "healthy" | "degraded" | "failing" | "paused" | "disabled";
export type DisabledReason =
  "admin" | "failing" | "receiver_gone" | "integration_off";

export interface EndpointDestination {
  host: string;
  display: string;
  fingerprint: string;
}

export interface EndpointStats24h {
  succeeded: number;
  failed: number;
  retrying: number;
  pending: number;
  cancelled: number;
  success_rate: number | null;
}

export interface WebhookEndpoint {
  id: string;
  kind: "http" | "zapier";
  description: string | null;
  destination: EndpointDestination;
  event_types: string[];
  status: EndpointStatus;
  health: EndpointHealth;
  paused_by_integration: boolean;
  disabled_reason: DisabledReason | null;
  disabled_at: string | null;
  consecutive_failures: number;
  failing_since: string | null;
  last_success_at: string | null;
  last_failure_at: string | null;
  last_delivery_at: string | null;
  secret_rotating_until: string | null;
  stats_24h: EndpointStats24h;
  created_via: string;
  created_at: string;
  updated_at: string;
  // Only on show, create and update.
  url?: string;
}

export interface WebhookEndpointsResponse {
  data: WebhookEndpoint[];
  meta: {
    limit: number;
    count: number;
    integration_enabled: boolean;
    generated_at: string;
  };
}

export interface FailureReason {
  category: string;
  title: string;
  hint: string | null;
}

export interface EndpointStatsDay {
  date: string;
  succeeded: number;
  failed: number;
  retrying: number;
  pending: number;
  cancelled: number;
}

export interface EndpointStats {
  endpoint_id: string;
  days: EndpointStatsDay[];
  failures_by_reason: (FailureReason & { count: number })[];
}

export interface EventTypeField {
  name: string;
  type: string;
  description: string;
}

export interface WebhookEventType {
  event_type: string;
  version: number;
  group: string;
  name: string;
  // What the fact means; when_it_fires says what makes it fire.
  description: string;
  when_it_fires: string;
  // The keys of the envelope's data object, in order.
  data_fields: EventTypeField[];
  // The records the event names that Sera's read API serves, with the fields
  // that name each and the API access Docs page that describes it.
  reads: EventTypeRead[];
  sample: Record<string, unknown>;
}

export interface EventTypeRead {
  resource: string;
  label: string;
  fields: string[];
  path: string;
  page: string;
  section: string | null;
}

export interface WebhooksConfig {
  enabled: boolean;
  enabled_at: string | null;
  disabled_at: string | null;
  disabled_reason: string | null;
  pending_deliveries_count: number;
  endpoints: { count: number; failing: number; disabled: number };
  read_only: boolean;
}

export interface EndpointParams {
  url?: string;
  description?: string;
  event_types?: string[];
}

export interface RevealedSecret {
  endpoint_id: string;
  secret: string;
  secret_rotating_until: string | null;
}

export interface RecoverResult {
  endpoint_id: string;
  // The effective time: clamped to the 90-day retention when `clamped`.
  since: string;
  clamped: boolean;
  queued: number;
}

export type EndpointTransition = "pause" | "resume" | "enable";

export type DeliveryStatus =
  "pending" | "retrying" | "succeeded" | "failed" | "cancelled";

export interface WebhookDelivery {
  id: string;
  event_id: string;
  event_type: string;
  endpoint: {
    id: string;
    description: string | null;
    host: string;
    archived: boolean;
  };
  status: DeliveryStatus;
  test: boolean;
  attempts_count: number;
  last_response_code: number | null;
  last_attempt_at: string | null;
  next_attempt_at: string | null;
  occurred_at: string;
  created_at: string;
  completed_at: string | null;
  cancelled_reason: string | null;
  outcome: FailureReason;
}

export interface DeliveryAttempt {
  number: number;
  started_at: string;
  response_code: number | null;
  outcome: FailureReason;
  response_excerpt: string | null;
}

export interface WebhookDeliveryDetail extends WebhookDelivery {
  request: {
    headers: Record<string, string | null>;
    body: unknown;
  };
  attempts: DeliveryAttempt[];
}

export interface DeliveryPagination {
  page: number;
  per_page: number;
  total_pages: number;
  total_objects: number;
  from: number;
  to: number;
}

export interface WebhookDeliveriesResponse {
  data: WebhookDelivery[];
  links: Record<string, string | null>;
  meta: { pagination: DeliveryPagination };
}

export interface DeliveryFilters {
  status?: DeliveryStatus | null;
  eventType?: string | null;
  endpointId?: string | null;
  since?: string | null;
  until?: string | null;
  q?: string | null;
}

export interface FetchDeliveriesParams extends DeliveryFilters {
  page?: number;
  perPage?: number;
}

const BASE = "/admin/v4/webhooks";
const QUIET = { headers: { ignoreError: true } };

async function get<T>(path: string): Promise<T> {
  const response = await API.makeRequest<T>(`${BASE}${path}`, QUIET);
  return response.data;
}

async function send<T>(
  method: "POST" | "PATCH" | "DELETE",
  path: string,
  data?: unknown
): Promise<T> {
  const response = await API.makeRequest<T>(`${BASE}${path}`, {
    ...QUIET,
    method,
    data
  });
  return response.data;
}

export default {
  async fetchWebhooksConfig(): Promise<WebhooksConfig> {
    return (await get<{ data: WebhooksConfig }>("/config")).data;
  },

  async updateWebhooksConfig(
    _: unknown,
    enabled: boolean
  ): Promise<WebhooksConfig> {
    return (
      await send<{ data: WebhooksConfig }>("PATCH", "/config", { enabled })
    ).data;
  },

  async fetchWebhookEventTypes(): Promise<WebhookEventType[]> {
    return (await get<{ data: WebhookEventType[] }>("/event_types")).data;
  },

  async fetchWebhookEndpoints(): Promise<WebhookEndpointsResponse> {
    return get<WebhookEndpointsResponse>("/endpoints");
  },

  async fetchWebhookEndpoint(_: unknown, id: string): Promise<WebhookEndpoint> {
    return (await get<{ data: WebhookEndpoint }>(`/endpoints/${id}`)).data;
  },

  async fetchWebhookEndpointStats(
    _: unknown,
    id: string
  ): Promise<EndpointStats> {
    return (await get<{ data: EndpointStats }>(`/endpoints/${id}/stats`)).data;
  },

  async createWebhookEndpoint(
    _: unknown,
    params: EndpointParams
  ): Promise<WebhookEndpoint> {
    return (await send<{ data: WebhookEndpoint }>("POST", "/endpoints", params))
      .data;
  },

  async updateWebhookEndpoint(
    _: unknown,
    { id, ...params }: EndpointParams & { id: string }
  ): Promise<WebhookEndpoint> {
    return (
      await send<{ data: WebhookEndpoint }>("PATCH", `/endpoints/${id}`, params)
    ).data;
  },

  async archiveWebhookEndpoint(_: unknown, id: string): Promise<void> {
    await send("DELETE", `/endpoints/${id}`);
  },

  async transitionWebhookEndpoint(
    _: unknown,
    { id, transition }: { id: string; transition: EndpointTransition }
  ): Promise<WebhookEndpoint> {
    return (
      await send<{ data: WebhookEndpoint }>(
        "POST",
        `/endpoints/${id}/${transition}`
      )
    ).data;
  },

  async rotateWebhookSecret(_: unknown, id: string): Promise<WebhookEndpoint> {
    return (
      await send<{ data: WebhookEndpoint }>(
        "POST",
        `/endpoints/${id}/rotate_secret`
      )
    ).data;
  },

  // Returned to the caller only, never committed: the secret lives as long
  // as the drawer that asked for it.
  async revealWebhookSecret(_: unknown, id: string): Promise<RevealedSecret> {
    return (
      await send<{ data: RevealedSecret }>(
        "POST",
        `/endpoints/${id}/reveal_secret`
      )
    ).data;
  },

  async sendWebhookTest(
    _: unknown,
    { id, eventType }: { id: string; eventType: string }
  ): Promise<unknown> {
    return (
      await send<{ data: unknown }>("POST", `/endpoints/${id}/test`, {
        event_type: eventType
      })
    ).data;
  },

  // Plain query params per the contract (status, event_type, endpoint_id,
  // since, until, q, page, per_page), not lib/pagination's filter[...].
  async fetchWebhookDeliveries(
    _: unknown,
    {
      status,
      eventType,
      endpointId,
      since,
      until,
      q,
      page = 1,
      perPage = 25
    }: FetchDeliveriesParams = {}
  ): Promise<WebhookDeliveriesResponse> {
    const query = new URLSearchParams({
      page: String(page),
      per_page: String(perPage)
    });
    if (status) query.set("status", status);
    if (eventType) query.set("event_type", eventType);
    if (endpointId) query.set("endpoint_id", endpointId);
    if (since) query.set("since", since);
    if (until) query.set("until", until);
    if (q) query.set("q", q);
    return get<WebhookDeliveriesResponse>(`/deliveries?${query.toString()}`);
  },

  async fetchWebhookDelivery(
    _: unknown,
    id: string
  ): Promise<WebhookDeliveryDetail> {
    return (await get<{ data: WebhookDeliveryDetail }>(`/deliveries/${id}`))
      .data;
  },

  async retryWebhookDelivery(_: unknown, id: string): Promise<WebhookDelivery> {
    return (
      await send<{ data: WebhookDelivery }>("POST", `/deliveries/${id}/retry`)
    ).data;
  },

  async recoverWebhookDeliveries(
    _: unknown,
    { id, since }: { id: string; since: string }
  ): Promise<RecoverResult> {
    return (
      await send<{ data: RecoverResult }>("POST", `/endpoints/${id}/recover`, {
        since
      })
    ).data;
  }
};
